<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

defineProps({
  headerType: { type: String, required: true },
  headerText: { type: String, default: '' },
  headerMediaFile: { type: Object, default: null },
  headerMediaPreviewUrl: { type: String, default: '' },
  headerAcceptAttr: { type: String, default: '*/*' },
  headerFileName: { type: String, default: '' },
  canInsertHeaderVariable: { type: Boolean, default: true },
});

const emit = defineEmits([
  'update:headerType',
  'update:headerText',
  'file-change',
  'clear-file',
  'insertHeaderVariable',
]);

const { t } = useI18n();
const headerInputRef = ref(null);
const fileInputRef = ref(null);

const onInsertVariable = () =>
  emit('insertHeaderVariable', headerInputRef.value);
</script>

<template>
  <div class="space-y-3">
    <div>
      <label class="block text-xs font-medium text-n-slate-11 mb-1">
        {{ t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TYPE') }}
      </label>
      <select
        :value="headerType"
        class="w-full h-9 px-3 rounded-lg outline outline-1 outline-n-weak bg-n-background text-sm text-n-slate-12 focus:outline-n-brand"
        @change="emit('update:headerType', $event.target.value)"
      >
        <option value="NONE">
          {{ t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TYPES.NONE') }}
        </option>
        <option value="TEXT">
          {{ t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TYPES.TEXT') }}
        </option>
        <option value="IMAGE">
          {{ t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TYPES.IMAGE') }}
        </option>
        <option value="DOCUMENT">
          {{ t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TYPES.DOCUMENT') }}
        </option>
        <option value="VIDEO">
          {{ t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TYPES.VIDEO') }}
        </option>
      </select>
    </div>

    <div v-if="headerType === 'TEXT'" class="space-y-2">
      <label class="block text-xs font-medium text-n-slate-11">
        {{ t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TEXT') }}
      </label>
      <input
        ref="headerInputRef"
        :value="headerText"
        type="text"
        class="w-full h-9 px-3 rounded-lg outline outline-1 outline-n-weak bg-transparent text-sm text-n-slate-12 focus:outline-n-brand"
        :placeholder="t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TEXT_PH')"
        @input="emit('update:headerText', $event.target.value)"
      />
      <div class="flex justify-end">
        <button
          type="button"
          class="h-8 px-3 rounded-lg text-xs bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!canInsertHeaderVariable"
          @click="onInsertVariable"
        >
          {{ t('INBOX_MGMT.TEMPLATES.CREATE.INSERT_VARIABLE') }}
        </button>
      </div>
    </div>

    <div
      v-else-if="['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerType)"
      class="space-y-2"
    >
      <label class="block text-xs font-medium text-n-slate-11">
        {{ t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_MEDIA_LABEL') }}
      </label>
      <input
        ref="fileInputRef"
        type="file"
        :accept="headerAcceptAttr"
        class="hidden"
        @change="emit('file-change', $event)"
      />

      <button
        v-if="!headerMediaFile"
        type="button"
        class="w-full border-2 border-dashed border-n-weak rounded-lg p-6 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-n-brand transition-colors"
        @click="fileInputRef?.click()"
      >
        <span class="text-n-slate-10 text-sm">
          {{ t('INBOX_MGMT.TEMPLATES.CREATE.CHOOSE_FILE') }}
        </span>
      </button>

      <div v-else class="space-y-2">
        <div
          v-if="headerType === 'IMAGE' && headerMediaPreviewUrl"
          class="rounded-lg overflow-hidden"
        >
          <img
            :src="headerMediaPreviewUrl"
            class="w-full max-h-40 object-cover rounded-lg"
            alt="header preview"
          />
        </div>
        <div v-else-if="headerType === 'VIDEO' && headerMediaPreviewUrl">
          <video
            :src="headerMediaPreviewUrl"
            controls
            class="w-full rounded-lg max-h-48"
          />
        </div>
        <div class="flex items-center justify-between">
          <span class="text-xs text-n-slate-10 truncate max-w-[70%]">{{
            headerFileName
          }}</span>
          <button
            type="button"
            class="h-7 px-2 rounded-md text-xs text-n-ruby-9 hover:bg-n-ruby-3"
            @click="emit('clear-file')"
          >
            {{ t('INBOX_MGMT.TEMPLATES.CREATE.REMOVE') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
